import React, { FC, useEffect, useState, useRef } from 'react';
import { Editor, EditorTextChangeEvent } from 'primereact/editor';
import { useTranslation } from 'localization';
import './TextEditorAtom.scss';

interface TextEditorAtomProps {
  value?: string; // Optional initial value for the editor
  onChange?: (value: string) => void; // Callback to handle text changes
  style?: React.CSSProperties; // Custom styles for the editor
  height?: string; // Custom height for the editor
  disabled?: boolean; // Whether the editor is disabled (view-only)
  showToolbarOnFocus?: boolean; // NEW: Whether to show toolbar only on focus (default: true)
  alwaysShowToolbar?: boolean; // NEW: Whether to always show the toolbar
  /** The id of the visible label that names the editing area. */
  ariaLabelledBy?: string;
  /** Moves focus into the editing area once the editor has loaded. */
  autoFocus?: boolean;
}

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Quill rebuilds every toolbar `<select>` into a picker: a `.ql-picker-label`
 * plus one `.ql-picker-item` per option, each `role="button"` and — in Quill
 * 2.0.3 — without an accessible name (colour swatches have no text at all).
 * That is the `aria-command-name` violation axe reports once Quill has
 * mounted, and `select-name` if axe runs before it has. The `<select>`s below
 * carry translated `aria-label`s so the pre-mount state is named; this pass
 * copies each label onto the picker Quill generated and names every item
 * from its option text or value, so the post-mount state is too.
 */
function labelQuillPickers(toolbar: HTMLElement, t: TFn): void {
  for (const picker of Array.from(toolbar.querySelectorAll<HTMLElement>('.ql-picker'))) {
    // Quill copies every attribute of the source <select> onto this span —
    // including our aria-label, which is prohibited on an element with no
    // role. Move it to the label that actually carries role="button".
    const groupLabel = picker.getAttribute('aria-label') ?? picker.dataset.groupLabel ?? '';
    if (picker.hasAttribute('aria-label')) {
      picker.dataset.groupLabel = groupLabel;
      picker.removeAttribute('aria-label');
    }
    const label = picker.querySelector<HTMLElement>('.ql-picker-label');
    if (label && groupLabel && !label.getAttribute('aria-label')) {
      label.setAttribute('aria-label', groupLabel);
    }
    const isColour = picker.classList.contains('ql-color-picker');
    for (const item of Array.from(picker.querySelectorAll<HTMLElement>('.ql-picker-item'))) {
      if (item.getAttribute('aria-label')) continue;
      const text = item.getAttribute('data-label');
      const value = item.getAttribute('data-value');
      let name = text ?? '';
      if (!name && isColour) name = value ? t('textEditor.colorSwatch', { value }) : t('textEditor.colorDefault');
      if (!name) name = value ? `${groupLabel} ${value}`.trim() : groupLabel;
      if (name) item.setAttribute('aria-label', name);
    }
  }
}

const TextEditorAtom: FC<TextEditorAtomProps> = ({
  value = '', // Default initial value
  onChange,
  style = {},
  height = '320px', // Default height
  disabled = false, // Default to not disabled
  showToolbarOnFocus = true, // Default to showing toolbar only on focus
  alwaysShowToolbar = false, // Default to not always showing toolbar
  ariaLabelledBy,
  autoFocus = false,
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState<string>(value);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const editorRef = useRef<Editor>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTextChange = (e: EditorTextChangeEvent) => {
    if (disabled) {
      return; // Do nothing if disabled
    }
    const newValue = e.htmlValue ?? ''; // Fallback to an empty string if null
    setText(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  // Quill builds its editing area (`quill.root`, a contenteditable) only once it has loaded, so its
  // name and focus are applied then.
  const handleLoad = (quill: { root?: HTMLElement; focus?: () => void } | null | undefined) => {
    const root = quill?.root;
    if (!root) return;
    if (ariaLabelledBy) {
      root.setAttribute('role', 'textbox');
      root.setAttribute('aria-multiline', 'true');
      root.setAttribute('aria-labelledby', ariaLabelledBy);
    }
    if (autoFocus && !disabled) quill.focus?.();
  };

  useEffect(() => {
    setText(value);
  }, [value]);

  // PrimeReact loads Quill lazily (`import('quill')`), so the pickers appear
  // some time after mount; observe the whole editor container so the names
  // land whenever Quill builds (or rebuilds) them.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    labelQuillPickers(container, t);
    const observer = new MutationObserver(() => labelQuillPickers(container, t));
    observer.observe(container, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [t]);

  // Determine whether to show the toolbar
  const shouldShowToolbar = alwaysShowToolbar || !showToolbarOnFocus || isFocused;

  // The same controls PrimeReact's default header renders, so nothing moves
  // visually — with a name on every control.
  const header = (
    <span role="toolbar" aria-label={t('textEditor.toolbar')}>
      <span className="ql-formats">
        <select className="ql-header" defaultValue="0" aria-label={t('textEditor.heading')}>
          <option value="1">{t('textEditor.headingOption')}</option>
          <option value="2">{t('textEditor.subheadingOption')}</option>
          <option value="0">{t('textEditor.normalOption')}</option>
        </select>
        <select className="ql-font" aria-label={t('textEditor.font')}>
          <option aria-label={t('textEditor.fontDefault')}></option>
          <option value="serif" aria-label={t('textEditor.fontSerif')}></option>
          <option value="monospace" aria-label={t('textEditor.fontMonospace')}></option>
        </select>
      </span>
      <span className="ql-formats">
        <button type="button" className="ql-bold" aria-label={t('textEditor.bold')}></button>
        <button type="button" className="ql-italic" aria-label={t('textEditor.italic')}></button>
        <button type="button" className="ql-underline" aria-label={t('textEditor.underline')}></button>
      </span>
      <span className="ql-formats">
        <select className="ql-color" aria-label={t('textEditor.color')}></select>
        <select className="ql-background" aria-label={t('textEditor.background')}></select>
      </span>
      <span className="ql-formats">
        <button type="button" className="ql-list" value="ordered" aria-label={t('textEditor.orderedList')}></button>
        <button type="button" className="ql-list" value="bullet" aria-label={t('textEditor.bulletList')}></button>
        <select className="ql-align" aria-label={t('textEditor.align')}>
          <option defaultValue="" aria-label={t('textEditor.alignLeft')}></option>
          <option value="center" aria-label={t('textEditor.alignCenter')}></option>
          <option value="right" aria-label={t('textEditor.alignRight')}></option>
          <option value="justify" aria-label={t('textEditor.alignJustify')}></option>
        </select>
      </span>
      <span className="ql-formats">
        <button type="button" className="ql-link" aria-label={t('textEditor.link')}></button>
        <button type="button" className="ql-image" aria-label={t('textEditor.image')}></button>
        <button type="button" className="ql-code-block" aria-label={t('textEditor.codeBlock')}></button>
      </span>
      <span className="ql-formats">
        <button type="button" className="ql-clean" aria-label={t('textEditor.clean')}></button>
      </span>
    </span>
  );

  return (
    <div
      ref={containerRef}
      className={`card ${disabled ? 'disabled-editor' : ''} ${shouldShowToolbar ? '' : 'hide-toolbar'}`}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Editor
        ref={editorRef}
        value={text}
        onTextChange={handleTextChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onLoad={handleLoad}
        style={{ ...style, height }}
        readOnly={disabled} // When disabled, the editor becomes read-only
        headerTemplate={header}
      />
    </div>
  );
};

export default TextEditorAtom;
